import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register Chart.js components (Pastikan ini ada di setiap komponen yang menggunakan Chart.js)
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

function SensorTanahPerairan() {
  // --- SEO Meta Tags untuk Halaman Sensor Tanah & Perairan ---
  useEffect(() => {
    document.title = "Sensor Tanah & Perairan - Solusi IoT Cerdas | Pertanian Cerdas & Kualitas Air";

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', "Optimalkan pertanian dengan pemantauan kelembaban, pH tanah, dan kualitas air. Solusi IoT cerdas untuk pertumbuhan tanaman dan ekosistem air.");

    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', "sensor tanah, sensor air, pH tanah, kelembaban tanah, suhu air, kekeruhan air, IoT pertanian, kualitas air");

    let linkCanonical = document.querySelector('link[rel="canonical"]');
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.setAttribute('rel', 'canonical');
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.setAttribute('href', "https://www.Solusi IoT Cerdas.com/produk/sensor/tanah-perairan"); // Ganti dengan URL halaman sebenarnya

    // Cleanup function: Akan dijalankan saat komponen di-unmount atau sebelum efek berikutnya
    return () => {
      document.title = "Solusi IoT Cerdas Solusi Cerdas IoT"; // Kembalikan ke judul default aplikasi
    };
  }, []); // Dependensi kosong agar useEffect hanya dijalankan sekali (saat mount)


  // Inisialisasi state dengan nilai awal yang valid
  const [soilMoisture, setSoilMoisture] = useState(65); // %
  const [soilPH, setSoilPH] = useState(6.2);
  const [waterPH, setWaterPH] = useState(7.0);
  const [waterTemp, setWaterTemp] = useState(24.5); // °C
  const [waterTurbidity, setWaterTurbidity] = useState(15); // NTU (Nephelometric Turbidity Units)

  // State untuk riwayat data harian (untuk grafik)
  const [soilMoistureHistory, setSoilMoistureHistory] = useState(() => {
    const history = [];
    const today = new Date();
    for (let i = 6; i >= 0; i--) { // Data untuk 7 hari terakhir
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const dayName = date.toLocaleDateString('id-ID', { weekday: 'short' });
      const value = parseFloat((55 + (6 - i) * 2 + Math.random() * 5).toFixed(1)); // Variasi kecil
      history.push({ day: dayName, date: date.toISOString().slice(0, 10), value: value });
    }
    return history;
  });

  const [waterPhHistory, setWaterPhHistory] = useState(() => {
    const history = [];
    const today = new Date();
    for (let i = 6; i >= 0; i--) { // Data untuk 7 hari terakhir
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const dayName = date.toLocaleDateString('id-ID', { weekday: 'short' });
      const value = parseFloat((6.8 + (6 - i) * 0.05 + Math.random() * 0.2).toFixed(1)); // Variasi kecil
      history.push({ day: dayName, date: date.toISOString().slice(0, 10), value: value });
    }
    return history;
  });


  useEffect(() => {
    const interval = setInterval(() => {
      const newTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

      // Update current levels
      setSoilMoisture(prev => parseFloat(Math.max(0, Math.min(100, prev + (Math.random() * 5 - 2.5))).toFixed(1)));
      setSoilPH(prev => parseFloat(Math.max(0, Math.min(14, prev + (Math.random() * 0.2 - 0.1))).toFixed(1)));
      setWaterPH(prev => parseFloat(Math.max(0, Math.min(14, prev + (Math.random() * 0.2 - 0.1))).toFixed(1)));
      setWaterTemp(prev => parseFloat(Math.max(0, Math.min(40, prev + (Math.random() * 1 - 0.5))).toFixed(1)));
      setWaterTurbidity(prev => parseFloat(Math.max(0, Math.min(200, prev + (Math.random() * 5 - 2.5))).toFixed(1)));

      // Update daily history for charts (adding small increments to "today's" value)
      setSoilMoistureHistory(prevHistory => {
        const updatedHistory = [...prevHistory];
        const todayIndex = updatedHistory.length - 1;
        const currentDayMoisture = updatedHistory[todayIndex];
        updatedHistory[todayIndex] = {
          ...currentDayMoisture,
          value: parseFloat((currentDayMoisture.value + (Math.random() * 0.5 - 0.25)).toFixed(1)) // small random change
        };
        return updatedHistory;
      });

      setWaterPhHistory(prevHistory => {
        const updatedHistory = [...prevHistory];
        const todayIndex = updatedHistory.length - 1;
        const currentDayPh = updatedHistory[todayIndex];
        updatedHistory[todayIndex] = {
          ...currentDayPh,
          value: parseFloat((currentDayPh.value + (Math.random() * 0.02 - 0.01)).toFixed(1)) // very small random change
        };
        return updatedHistory;
      });

    }, 5000); // Update setiap 5 detik

    return () => clearInterval(interval); // Cleanup interval on unmount
  }, []); // Dependensi kosong agar hanya menjalankan sekali (initial set history)

  // Data dan opsi untuk grafik Kelembaban Tanah
  const soilMoistureChartData = {
    labels: soilMoistureHistory.map(data => data.day),
    datasets: [
      {
        label: 'Kelembaban Tanah Harian (%)',
        data: soilMoistureHistory.map(data => data.value),
        fill: true,
        backgroundColor: 'rgba(16, 185, 129, 0.2)', // green-500 with opacity
        borderColor: '#10b981', // green-500
        tension: 0.3,
        pointBackgroundColor: '#10b981',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: '#10b981',
      },
    ],
  };

  const soilMoistureChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: 'var(--text-color)',
        },
      },
      title: {
        display: true,
        text: 'Riwayat Kelembaban Tanah 7 Hari Terakhir',
        color: 'var(--text-color)',
        font: {
          size: 18,
        },
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += new Intl.NumberFormat('id-ID', { maximumFractionDigits: 1 }).format(context.parsed.y) + '%';
            }
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        ticks: { color: 'var(--text-color)' },
        grid: { color: 'rgba(100, 100, 100, 0.1)' },
      },
      y: {
        ticks: {
          color: 'var(--text-color)',
          callback: function(value) { return value + '%'; }
        },
        grid: { color: 'rgba(100, 100, 100, 0.1)' },
        min: 0,
        max: 100,
      },
    },
  };

  // Data dan opsi untuk grafik pH Air
  const waterPhChartData = {
    labels: waterPhHistory.map(data => data.day),
    datasets: [
      {
        label: 'pH Air Harian',
        data: waterPhHistory.map(data => data.value),
        fill: true,
        backgroundColor: 'rgba(59, 130, 246, 0.2)', // blue-500 with opacity
        borderColor: '#3b82f6', // blue-500
        tension: 0.3,
        pointBackgroundColor: '#3b82f6',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: '#3b82f6',
      },
    ],
  };

  const waterPhChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: 'var(--text-color)',
        },
      },
      title: {
        display: true,
        text: 'Riwayat pH Air 7 Hari Terakhir',
        color: 'var(--text-color)',
        font: {
          size: 18,
        },
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += new Intl.NumberFormat('id-ID', { maximumFractionDigits: 1 }).format(context.parsed.y);
            }
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        ticks: { color: 'var(--text-color)' },
        grid: { color: 'rgba(100, 100, 100, 0.1)' },
      },
      y: {
        ticks: { color: 'var(--text-color)' },
        grid: { color: 'rgba(100, 100, 100, 0.1)' },
        min: 0, // pH skala 0-14
        max: 14,
      },
    },
  };


  return (
    <div className="container mx-auto p-4 md:p-8 bg-gray-50 text-gray-800 font-sans
                    dark:bg-gray-900 dark:text-gray-200 transition-colors duration-500">
      {/* Hero Section */}
      <section
        className="relative bg-gradient-to-br from-green-600 to-teal-700 text-white rounded-lg shadow-xl p-6 md:p-12 mb-8 text-center overflow-hidden
                   dark:from-green-800 dark:to-teal-900 transition-colors duration-500"
        style={{ backgroundImage: 'url(./assets/images/hero-soil-water.png)', backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="absolute inset-0 bg-black opacity-40 rounded-lg"></div>
        <div className="relative z-10">
          <h1 className="text-3xl md:text-5xl font-extrabold mb-4 animate-fade-in-down">
            Lahan Subur, Perairan Terjaga: Sensor Tanah & Perairan Solusi IoT Cerdas
          </h1>
          <p className="text-lg md:text-xl max-w-3xl mx-auto mb-8 animate-fade-in">
            Dapatkan wawasan mendalam tentang kondisi tanah dan kualitas air Anda.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            <div className="bg-white text-green-800 rounded-lg p-6 shadow-md flex-1 transform hover:scale-105 transition-all duration-300
                            dark:bg-gray-800 dark:text-green-200">
              <h3 className="text-xl font-semibold mb-2">Kelembaban Tanah</h3>
              <h2 className="text-4xl font-bold text-green-600 dark:text-green-400">{soilMoisture}%</h2>
              <p className="text-sm text-gray-600 mt-2 dark:text-gray-400">Kondisi Lahan</p>
            </div>
            <div className="bg-white text-green-800 rounded-lg p-6 shadow-md flex-1 transform hover:scale-105 transition-all duration-300
                            dark:bg-gray-800 dark:text-green-200">
              <h3 className="text-xl font-semibold mb-2">pH Tanah</h3>
              <h2 className="text-4xl font-bold text-green-600 dark:text-green-400">{soilPH}</h2>
              <p className="text-sm text-gray-600 mt-2 dark:text-gray-400">Level Keasaman</p>
            </div>
            <div className="bg-white text-blue-800 rounded-lg p-6 shadow-md flex-1 transform hover:scale-105 transition-all duration-300
                            dark:bg-gray-800 dark:text-blue-200">
              <h3 className="text-xl font-semibold mb-2">pH Air</h3>
              <h2 className="text-4xl font-bold text-blue-600 dark:text-blue-400">{waterPH}</h2>
              <p className="text-sm text-gray-600 mt-2 dark:text-gray-400">Kualitas Air</p>
            </div>
          </div>
          {/* Menambahkan tampilan Suhu dan Kekeruhan Air di bawah pH Air */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <div className="bg-white text-blue-800 rounded-lg p-6 shadow-md flex-1 transform hover:scale-105 transition-all duration-300
                            dark:bg-gray-800 dark:text-blue-200">
              <h3 className="text-xl font-semibold mb-2">Suhu Air</h3>
              <h2 className="text-4xl font-bold text-blue-600 dark:text-blue-400">{waterTemp}°C</h2>
              <p className="text-sm text-gray-600 mt-2 dark:text-gray-400">Derajat Celcius</p>
            </div>
            <div className="bg-white text-blue-800 rounded-lg p-6 shadow-md flex-1 transform hover:scale-105 transition-all duration-300
                            dark:bg-gray-800 dark:text-blue-200">
              <h3 className="text-xl font-semibold mb-2">Kekeruhan Air</h3>
              <h2 className="text-4xl font-bold text-blue-600 dark:text-blue-400">{waterTurbidity} NTU</h2>
              <p className="text-sm text-gray-600 mt-2 dark:text-gray-400">Unit Kekeruhan Nephelometric</p>
            </div>
          </div>
        </div>
      </section>

      {/* Bagian Grafik Kelembaban Tanah */}
      <section className="bg-white rounded-lg shadow-md p-6 md:p-8 mb-8
                          dark:bg-gray-800 dark:shadow-xl transition-colors duration-500">
        <h2 className="text-3xl font-bold text-gray-900 text-center mb-6 dark:text-gray-100">
          Tren Kelembaban Tanah Anda
        </h2>
        <div className="h-80 w-full"> {/* Atur tinggi grafik */}
          <Line data={soilMoistureChartData} options={soilMoistureChartOptions} />
        </div>
        <p className="text-center text-gray-600 text-sm mt-4 dark:text-gray-400">
          Data ini disimulasikan dan diperbarui secara *real-time*.
        </p>
      </section>

      {/* Bagian Grafik pH Air */}
      <section className="bg-white rounded-lg shadow-md p-6 md:p-8 mb-8
                          dark:bg-gray-800 dark:shadow-xl transition-colors duration-500">
        <h2 className="text-3xl font-bold text-gray-900 text-center mb-6 dark:text-gray-100">
          Tren Kualitas pH Air Anda
        </h2>
        <div className="h-80 w-full"> {/* Atur tinggi grafik */}
          <Line data={waterPhChartData} options={waterPhChartOptions} />
        </div>
        <p className="text-center text-gray-600 text-sm mt-4 dark:text-gray-400">
          Data ini disimulasikan dan diperbarui secara *real-time*.
        </p>
      </section>

      {/* Why Monitor Section */}
      <section className="bg-white rounded-lg shadow-md p-6 md:p-8 mb-8
                          dark:bg-gray-800 dark:shadow-xl transition-colors duration-500">
        <h2 className="text-3xl font-bold text-green-700 text-center mb-6 dark:text-green-400">Fondasi Pertumbuhan & Ekosistem Sehat</h2>
        <p className="text-lg text-gray-700 text-center mb-6 max-w-3xl mx-auto dark:text-gray-300">
          Memahami kondisi tanah dan kualitas air adalah kunci untuk pertanian yang produktif.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
          <div className="bg-green-50 p-6 rounded-lg text-center shadow-sm transition-all duration-300 transform hover:-translate-y-1 hover:shadow-md
                          dark:bg-gray-700 dark:shadow-lg">
            <img src="./assets/icons/icon-optimal-growth.png" alt="Optimal Growth Icon" className="mx-auto mb-4 w-16 h-16 animate-bounce-subtle" />
            <h3 className="text-xl font-semibold text-green-600 mb-2 dark:text-green-300">Pertumbuhan Tanaman Optimal</h3>
            <p className="text-gray-700 dark:text-gray-300">Pastikan tanaman mendapatkan nutrisi dan kelembaban yang tepat.</p>
          </div>
          <div className="bg-green-50 p-6 rounded-lg text-center shadow-sm transition-all duration-300 transform hover:-translate-y-1 hover:shadow-md
                          dark:bg-gray-700 dark:shadow-lg">
            <img src="./assets/icons/icon-save-resources.png" alt="Save Resources Icon" className="mx-auto mb-4 w-16 h-16 animate-bounce-subtle" />
            <h3 className="text-xl font-semibold text-green-600 mb-2 dark:text-green-300">Hemat Sumber Daya</h3>
            <p className="text-gray-700 dark:text-gray-300">Optimalkan penggunaan air dan pupuk.</p>
          </div>
          <div className="bg-green-50 p-6 rounded-lg text-center shadow-sm transition-all duration-300 transform hover:-translate-y-1 hover:shadow-md
                          dark:bg-gray-700 dark:shadow-lg">
            <img src="./assets/icons/icon-ecosystem-health.png" alt="Ecosystem Health Icon" className="mx-auto mb-4 w-16 h-16 animate-bounce-subtle" />
            <h3 className="text-xl font-semibold text-green-600 mb-2 dark:text-green-300">Kesehatan Ekosistem</h3>
            <p className="text-gray-700 dark:text-gray-300">Jaga kualitas air untuk ikan, hewan, dan lingkungan.</p>
          </div>
          <div className="bg-green-50 p-6 rounded-lg text-center shadow-sm transition-all duration-300 transform hover:-translate-y-1 hover:shadow-md
                          dark:bg-gray-700 dark:shadow-lg">
            <img src="./assets/icons/icon-early-detection.png" alt="Early Detection Icon" className="mx-auto mb-4 w-16 h-16 animate-bounce-subtle" />
            <h3 className="text-xl font-semibold text-green-600 mb-2 dark:text-green-300">Deteksi Dini Masalah</h3>
            <p className="text-gray-700 dark:text-gray-300">Identifikasi potensi penyakit tanaman atau polusi air.</p>
          </div>
        </div>
      </section>

      {/* Soil Sensors Section */}
      <section className="bg-white rounded-lg shadow-md p-6 md:p-8 mb-8
                          dark:bg-gray-800 dark:shadow-xl transition-colors duration-500">
        <h2 className="text-3xl font-bold text-green-700 text-center mb-6 dark:text-green-400">Pahami Kesehatan Tanah Anda: Sensor Tanah</h2>
        <p className="text-lg text-gray-700 text-center mb-6 max-w-3xl mx-auto dark:text-gray-300">
          Solusi IoT Cerdas mendukung berbagai sensor tanah untuk memberikan gambaran lengkap tentang lingkungan pertumbuhan tanaman Anda.
        </p>
        <div className="flex flex-col md:flex-row items-center gap-8">
          <div className="w-full md:w-1/2">
            <img src="./assets/images/soil-sensor-illus.png" alt="Soil Sensor Illustration" className="rounded-lg shadow-lg w-full h-auto" />
          </div>
          <ul className="list-none p-0 w-full md:w-1/2 space-y-4">
            <li className="bg-green-50 p-4 rounded-md border-l-4 border-green-500 shadow-sm flex items-center gap-3
                            dark:bg-gray-700 dark:border-green-700 dark:text-gray-200 dark:shadow-lg transition-colors duration-500">
              <img src="./assets/icons/icon-moisture.png" alt="Moisture Icon" className="w-8 h-8 flex-shrink-0" />
              <div>
                <strong className="text-green-800 dark:text-green-300">Kelembaban Tanah</strong>: Untuk mengetahui kapan harus menyiram.
              </div>
            </li>
            <li className="bg-green-50 p-4 rounded-md border-l-4 border-green-500 shadow-sm flex items-center gap-3
                            dark:bg-gray-700 dark:border-green-700 dark:text-gray-200 dark:shadow-lg transition-colors duration-500">
              <img src="./assets/icons/icon-pH.png" alt="pH Icon" className="w-8 h-8 flex-shrink-0" />
              <div>
                <strong className="text-green-800 dark:text-green-300">pH Tanah</strong>: Mengukur tingkat keasaman atau kebasaan, krusial untuk penyerapan nutrisi.
              </div>
            </li>
            <li className="bg-green-50 p-4 rounded-md border-l-4 border-green-500 shadow-sm flex items-center gap-3
                            dark:bg-gray-700 dark:border-green-700 dark:text-gray-200 dark:shadow-lg transition-colors duration-500">
              <img src="./assets/icons/icon-NPK.png" alt="NPK Icon" className="w-8 h-8 flex-shrink-0" />
              <div>
                <strong className="text-green-800 dark:text-green-300">NPK (Nitrogen, Fosfor, Kalium)</strong>: Data nutrisi mikro yang vital untuk pertumbuhan optimal.
              </div>
            </li>
            <li className="bg-green-50 p-4 rounded-md border-l-4 border-green-500 shadow-sm flex items-center gap-3
                            dark:bg-gray-700 dark:border-green-700 dark:text-gray-200 dark:shadow-lg transition-colors duration-500">
              <img src="./assets/icons/icon-chart-line.png" alt="Chart Icon" className="w-8 h-8 flex-shrink-0" />
              <div>
                <strong className="text-green-800 dark:text-green-300">Grafik Tren Kesuburan</strong>: Lacak perubahan kondisi tanah dari waktu ke waktu.
              </div>
            </li>
          </ul>
        </div>
      </section>

      {/* Water Sensors Section */}
      <section className="bg-white rounded-lg shadow-md p-6 md:p-8 mb-8
                          dark:bg-gray-800 dark:shadow-xl transition-colors duration-500">
        <h2 className="text-3xl font-bold text-blue-700 text-center mb-6 dark:text-blue-400">Jaga Kualitas Air Anda: Sensor Perairan</h2>
        <p className="text-lg text-gray-700 text-center mb-6 max-w-3xl mx-auto dark:text-gray-300">
          Untuk pemantauan perairan, Solusi IoT Cerdas dapat terhubung dengan sensor yang mengukur parameter kunci kualitas air.
        </p>
        <div className="flex flex-col-reverse md:flex-row items-center gap-8">
          <ul className="list-none p-0 w-full md:w-1/2 space-y-4">
            <li className="bg-blue-50 p-4 rounded-md border-l-4 border-blue-500 shadow-sm flex items-center gap-3
                            dark:bg-gray-700 dark:border-blue-700 dark:text-gray-200 dark:shadow-lg transition-colors duration-500">
              <img src="./assets/icons/icon-pH.png" alt="pH Icon" className="w-8 h-8 flex-shrink-0" />
              <div>
                <strong className="text-blue-800 dark:text-blue-300">pH Air</strong>: Indikator penting kesehatan air, mempengaruhi kehidupan akuatik.
              </div>
            </li>
            <li className="bg-blue-50 p-4 rounded-md border-l-4 border-blue-500 shadow-sm flex items-center gap-3
                            dark:bg-gray-700 dark:border-blue-700 dark:text-gray-200 dark:shadow-lg transition-colors duration-500">
              <img src="./assets/icons/icon-temp-water.png" alt="Temperature Icon" className="w-8 h-8 flex-shrink-0" />
              <div>
                <strong className="text-blue-800 dark:text-blue-300">Suhu Air</strong>: Krusial untuk pertumbuhan ikan, aktivitas biologis, dan kontrol proses.
              </div>
            </li>
            <li className="bg-blue-50 p-4 rounded-md border-l-4 border-blue-500 shadow-sm flex items-center gap-3
                            dark:bg-gray-700 dark:border-blue-700 dark:text-gray-200 dark:shadow-lg transition-colors duration-500">
              <img src="./assets/icons/icon-turbidity.png" alt="Turbidity Icon" className="w-8 h-8 flex-shrink-0" />
              <div>
                <strong className="text-blue-800 dark:text-blue-300">Kekeruhan Air</strong>: Mengukur kejernihan air, indikator adanya partikel tersuspensi atau polutan.
              </div>
            </li>
            <li className="bg-blue-50 p-4 rounded-md border-l-4 border-blue-500 shadow-sm flex items-center gap-3
                            dark:bg-gray-700 dark:border-blue-700 dark:text-gray-200 dark:shadow-lg transition-colors duration-500">
              <img src="./assets/icons/icon-chart-line.png" alt="History Icon" className="w-8 h-8 flex-shrink-0" />
              <div>
                <strong className="text-blue-800 dark:text-blue-300">Riwayat Data Akuatik</strong>: Lacak kualitas air untuk pemeliharaan berkelanjutan.
              </div>
            </li>
          </ul>
          <div className="w-full md:w-1/2">
            <img src="./assets/images/water-sensor-illus.png" alt="Water Sensor Illustration" className="rounded-lg shadow-lg w-full h-auto" />
          </div>
        </div>
      </section>

      {/* Combined Scenarios Section */}
      <section className="bg-white rounded-lg shadow-md p-6 md:p-8 mb-8
                          dark:bg-gray-800 dark:shadow-xl transition-colors duration-500">
        <h2 className="text-3xl font-bold text-indigo-700 text-center mb-6 dark:text-indigo-400">Solusi Holistik untuk Lahan & Perairan</h2>
        <p className="text-lg text-gray-700 text-center mb-6 max-w-3xl mx-auto dark:text-gray-300">
          Kombinasi sensor tanah dan perairan melalui Solusi IoT Cerdas memungkinkan sistem manajemen lingkungan yang lebih cerdas dan terintegrasi:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-purple-50 p-6 rounded-lg shadow-sm transition-all duration-300 transform hover:-translate-y-1 hover:shadow-md
                          dark:bg-gray-700 dark:shadow-lg">
            <h3 className="text-xl font-semibold text-purple-700 mb-2 dark:text-purple-400">Sistem Irigasi Cerdas</h3>
            <p className="text-gray-700 dark:text-gray-300">Berdasarkan kelembaban tanah dan data cuaca, pompa air aktif secara otomatis.</p>
          </div>
          <div className="bg-purple-50 p-6 rounded-lg shadow-sm transition-all duration-300 transform hover:-translate-y-1 hover:shadow-md
                          dark:bg-gray-700 dark:shadow-lg">
            <h3 className="text-xl font-semibold text-purple-700 mb-2 dark:text-purple-400">Akuaponik/Hidroponik Otomatis</h3>
            <p className="text-gray-700 dark:text-gray-300">Pantau pH air dan nutrisi tanah (media tanam), sesuaikan pemberian pupuk atau pH secara otomatis.</p>
          </div>
          <div className="bg-purple-50 p-6 rounded-lg shadow-sm transition-all duration-300 transform hover:-translate-y-1 hover:shadow-md col-span-1 md:col-span-2
                          dark:bg-gray-700 dark:shadow-lg">
            <h3 className="text-xl font-semibold text-purple-700 mb-2 dark:text-purple-400">Pemantauan Lingkungan Terpadu</h3>
            <p className="text-gray-700 dark:text-gray-300">Pantau dampak pengairan terhadap kualitas air tanah atau air buangan.</p>
          </div>
        </div>
      </section>

      {/* Getting Started Section */}
      <section className="bg-white rounded-lg shadow-md p-6 md:p-8 text-center mb-8
                          dark:bg-gray-800 dark:shadow-xl transition-colors duration-500">
        <h2 className="text-3xl font-bold text-indigo-700 mb-6 dark:text-indigo-400">Siap Bertani Cerdas atau Mengelola Perairan?</h2>
        <p className="text-lg text-gray-700 mb-6 max-w-2xl mx-auto dark:text-gray-300">
          Integrasi sensor tanah dan perairan ke Solusi IoT Cerdas adalah langkah transformatif untuk pengelolaan sumber daya yang lebih efisien dan berkelanjutan.
        </p>
        <button className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-8 rounded-lg text-xl
                           transition-all duration-300 ease-in-out transform hover:scale-105"
                onClick={() => alert('Link ke Panduan Instalasi atau halaman Pendaftaran!')}>
          Pelajari Panduan Instalasi & Coba Solusi IoT Cerdas Sekarang
        </button>
      </section>
    </div>
  );
}

export default SensorTanahPerairan;